'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import Grid4x4Icon from '@mui/icons-material/Grid4x4';
import LinkIcon from '@mui/icons-material/Link';
import { Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { debounce } from 'lodash-es';
import { useCallback } from 'react';
import { DataGrid, DataGridSortModel, FilterSearch, Badge, Spinner } from '@repo/components';
import { Role, RolesApiResponse } from '@repo/shared/domain';
import { guardedPost, constructQuery, sortToString, stringToSort } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { guardedClient as clientRequest } from '@repo/shared/helpers';

interface RolesSearch {
	sort?: string;
	page?: number;
	search?: string;
}

const getRoles = async (query: string): Promise<RolesApiResponse> => {
	const { body } = await clientRequest.get<undefined, RolesApiResponse>(`/api/roles?${query}`, { schema: undefined });
	return body;
};

export const Route = createFileRoute('/_admin/config/roles/')({
	validateSearch: (search: Record<string, unknown>): RolesSearch => ({
		sort: search.sort as string | undefined,
		page: search.page as number | undefined,
		search: search.search as string | undefined,
	}),
	component: RolesPage,
});

const accessTypes = {
	permanent: 'Permanent',
	event: 'Temporary (Event-Based)',
	schedule: 'Temporary (Schedule)',
	single: 'Temporary (Single-Entry)',
} as const;

const columns = [
	{
		field: 'parentRoleIds',
		headerName: '',
		width: 30,
		renderCell: (cell: { value?: string[] }) =>
			cell.value?.length ? (
				<Tooltip title="Validity linked to other roles">
					<span>
						<LinkIcon />
					</span>
				</Tooltip>
			) : (
				''
			),
	},
	{
		field: 'name',
		headerName: 'Name',
		flex: 2,
	},
	{
		field: 'taskDetails',
		headerName: 'Tasks',
		flex: 3,
		renderCell: (cell: { value: { id: string; name: string }[] }) =>
			cell.value.map((row) => <Badge key={row.id}>{row.name}</Badge>),
		sortable: false,
	},
	{
		field: 'zones',
		headerName: 'Site Access',
		flex: 3,
		sortable: false,
		renderCell: (cell: { value: { id: string; name: string }[] }) => {
			if (!cell?.value?.length) {
				return null;
			}

			return (
				<>
					<Badge key={cell.value[0].id}>{cell.value[0].name}</Badge>
					{cell.value.length > 1 && (
						<Badge>
							+ {cell.value.length - 1} other zone
							{cell.value.length - 1 === 1 ? '' : 's'}
						</Badge>
					)}
				</>
			);
		},
	},
	{
		field: 'accessType',
		headerName: 'Access Type',
		flex: 1,
		renderCell: (cell: { row: { showAccessCode?: string }; value?: keyof typeof accessTypes }) =>
			['always', 'activeRole'].includes(cell?.row?.showAccessCode || '')
				? accessTypes[cell?.value || 'permanent'] || '-'
				: '',
	},
	{
		field: 'usesManager',
		headerName: 'Manager Access',
		flex: 1,
		renderCell: (cell: { value?: boolean }) => (cell.value ? <CheckIcon /> : ''),
	},
];

function RolesPage() {
	const searchParams = Route.useSearch();
	const { sort, page, search } = searchParams;
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const updateQueryParams = useUpdateQueryParams();

	useBreadcrumbLevel(0, 'Configuration');
	useBreadcrumbLevel(1, 'Roles');

	const query = constructQuery({
		sort: sort || 'name:asc',
		page,
		search,
		noPagination: 'false',
	});

	const { data: rolesData, isLoading } = useQuery({
		queryKey: ['roles', query],
		queryFn: () => getRoles(query),
	});

	const data = rolesData || { data: [], count: 0, page: 0, pageSize: 25 };

	const handleRowClick = ({ row }: { row: Role }) => {
		navigate({ to: '/config/roles/$roleId', params: { roleId: row.id } });
	};

	const handleAddUserRole = useCallback(async () => {
		const response = await guardedPost<Role>('/api/roles/', {});

		if (response.body?.id) {
			navigate({ to: '/config/roles/$roleId', params: { roleId: response.body.id } });
		}
	}, [navigate]);

	const handleSort = ([sortItem]: DataGridSortModel) => {
		updateQueryParams({ sort: sortItem ? sortToString(sortItem) : undefined });
	};

	const handlePaginationModelChange = ({ page: newPage }: { page: number }) => {
		updateQueryParams({ page: newPage });
	};

	const handleSearchChange = (searchValue: string) => {
		updateQueryParams({ search: searchValue });
	};

	const revalidateRoles = () => {
		queryClient.invalidateQueries({ queryKey: ['roles'] });
	};

	return (
		<>
			<div className="p-4">
				<div key="search" className="flex-grow flex justify-between gap-2">
					<FilterSearch
						label="Search Role"
						defaultValue={search || ''}
						onChange={debounce((searchInput: string) => {
							handleSearchChange(searchInput);
						}, 350)}
						sx={{ minWidth: 360, width: 'auto' }}
					/>
					<div className="flex gap-2">
						<Button
							onClick={() => navigate({ to: '/config/roles/access' })}
							startIcon={<Grid4x4Icon />}
							variant="outlined"
							key="accessGrid"
							className="flex-shrink-0"
						>
							Access Grid
						</Button>
						<Button
							onClick={handleAddUserRole}
							startIcon={<AddIcon />}
							variant="outlined"
							key="addUserRole"
							className="flex-shrink-0"
						>
							Add User Role
						</Button>
					</div>
				</div>
				<Paper variant="outlined" sx={{ marginTop: 2 }}>
					<DataGrid
						isLoading={isLoading}
						data={data?.data || []}
						columns={columns}
						paginationMode="server"
						sortingMode="server"
						sortingOrder={['asc', 'desc']}
						pagination
						onRowClick={handleRowClick}
						onReload={revalidateRoles}
						pageSize={data?.pageSize || 0}
						pageSizeOptions={[data?.pageSize || 0]}
						onPaginationModelChange={handlePaginationModelChange}
						sortModel={[stringToSort(sort || 'name:asc')]}
						page={data?.page || 0}
						rowCount={data?.count || 0}
						onSortModelChange={handleSort}
					/>
				</Paper>
			</div>
		</>
	);
}
